varying vec2 vUv;
uniform vec3 u_bgColor;
uniform vec3 u_sphereCol;
uniform vec3 u_size;
uniform vec3 u_mouse;
uniform float u_pixelRatio;
uniform float u_time;
uniform float u_camDist;

#define MAX_STEPS 80
#define MAX_DIST 80.0
#define MIN_DIST 0.0002
#define PI 3.14159265359

vec3 permute(vec3 x) { return mod(((x*34.0)+1.0)*x, 289.0); }

float snoise(vec2 v) {
    const vec4 C = vec4(0.211324865405187, 0.366025403784439, -0.577350269189626, 0.024390243902439);
    vec2 i  = floor(v + dot(v, C.yy) );
    vec2 x0 = v - i + dot(i, C.xx);
    vec2 i1 = (x0.x > x0.y) ? vec2(1.0, 0.0) : vec2(0.0, 1.0);
    vec4 x12 = x0.xyxy + C.xxzz;
    x12.xy -= i1;
    i = mod(i, 289.0);
    vec3 p = permute( permute( i.y + vec3(0.0, i1.y, 1.0 )) + i.x + vec3(0.0, i1.x, 1.0 ));
    vec3 m = max(0.5 - vec3(dot(x0, x0), dot(x12.xy, x12.xy), dot(x12.zw, x12.zw)), 0.0);
    m = m * m;
    m = m * m;
    vec3 x = 2.0 * fract(p * C.www) - 1.0;
    vec3 h = abs(x) - 0.5;
    vec3 ox = floor(x + 0.5);
    vec3 a0 = x - ox;
    m *= 1.79284291400159 - 0.85373472095314 * (a0 * a0 + h * h);
    vec3 g;
    g.x = a0.x * x0.x + h.x * x0.y;
    g.yz = a0.yz * x12.xz + h.yz * x12.yw;
    return 130.0 * dot(m, g);
}

float sdSphere(vec3 p, float s) {
    return length(p) - s;
}

float smin(float a, float b, float k) {
    k *= 6.0;
    float h = max(k - abs(a - b), 0.0) / k;
    return min(a, b) - h * h * h * k * (1.0 / 6.0);
}
vec3 smoothRepel(vec3 spherePos, vec3 mousePos, float strength, float smoothFactor) {
     float minDist = 0.3;
    vec3 dir = spherePos - mousePos;
    float dist = max(length(dir), minDist);
    dir = normalize(dir);
    float repulsion = 1.0 - smoothstep(0.0, 2.0, dist);
    repulsion = pow(repulsion, 3.0); 
    vec3 newPos = spherePos + dir * repulsion * strength;
    
    // Interpolation pour lisser la transition
    return mix(spherePos, newPos, smoothFactor);
}

float map(vec3 p) {
    float noise = snoise(vec2(10., u_time * 0.2)) * 1.0;
    float noiseTwo = snoise(vec2(20., u_time * 0.2)) * 0.8;
    float noiseThree = snoise(vec2(30., u_time * 0.2)) * 0.6;

    vec3 mousePos = vec3(
        u_mouse.x * u_size.z * (-u_camDist) * (1. / u_size.z),
        u_mouse.y * (-u_camDist) * (1. / u_size.z),
        0.0
    );
    float influence = 1.-smoothstep(0.2, 2., length(mousePos)); 

    float smoothFactor = 0.05; 
    float repulsionStrength = 4.;

    vec3 sphereBasePos = vec3(-0.4, 0., 0.);
    vec3 spherePos = vec3(cos(u_time) * noise, sin(u_time) * noise, 0.0);
    spherePos += mousePos*0.2;
    vec3 finalSpherePos = mix(sphereBasePos, spherePos, influence);
    float sphere = sdSphere(p - finalSpherePos, 0.2);

    vec3 sphere2BasePos = vec3(0.28, 0., 0.);
    vec3 spherePos2 = vec3(cos(u_time + 10.0) * noiseTwo, sin(u_time + 10.0) * noiseTwo, 0.1);
    spherePos2 -= mousePos*0.2;
    vec3 finalSphere2Pos = mix(sphere2BasePos, spherePos2, influence);
    float sphere2 = sdSphere(p - finalSphere2Pos, 0.2);

    vec3 sphere3BasePos = vec3(0., 0.3, 0.);
    vec3 spherePos3 = vec3(cos(u_time + 20.0) * noiseThree, sin(u_time + 20.0) * noiseThree, 0.0);
    spherePos2 *= mousePos*0.2;
    vec3 finalSphere3Pos = mix(sphere3BasePos, spherePos3, influence);

    float sphere3 = sdSphere(p - finalSphere3Pos, 0.2);

    float res = smin(sphere, sphere2, 0.02);
    res = smin(res, sphere3, 0.02);

    return res;
}

vec3 calcNormal(vec3 p) {
    const float eps = 0.0001;
    const vec2 h = vec2(eps, 0);
    return normalize(vec3(
        map(p + h.xyy) - map(p - h.xyy),
        map(p + h.yxy) - map(p - h.yxy),
        map(p + h.yyx) - map(p - h.yyx)
    ));
}

float rayMarch(vec3 ro, vec3 rd) {
    float dist = 0.0;
    for (int i = 0; i < MAX_STEPS; i++) {
        vec3 itPos = ro + rd * dist;
        float itDist = map(itPos);
        dist += itDist;
        if (dist > MAX_DIST || itDist < MIN_DIST) break;
    }
    return dist;
}

vec3 directionalLight(vec3 lightColor, float lightIntensity, vec3 normal, vec3 lightPosition, vec3 viewDirection, float specularPower) {
    vec3 lightDirection = normalize(lightPosition);
    normal = normalize(normal);
    float shading = dot(normal, lightDirection);
    shading = clamp(shading, 0.0, 1.0);
    vec3 lightReflection = reflect(-lightDirection, normal);
    float specular = dot(lightReflection, viewDirection);
    specular = clamp(specular, 0.0, 1.0);
    specular = pow(specular, specularPower);
    return (lightColor * lightIntensity) * (shading);
}

vec3 ambientLight(vec3 lightColor, float lightIntensity) {
    return lightColor * lightIntensity;
}

void main() {
    vec2 uv = (vUv * 2.0) - 1.0;
    uv.x *= u_size.z;
    vec3 ro = vec3(0.0, 0.0, u_camDist);
    vec3 rd = normalize(vec3(uv.x * (1.0 / u_size.z), uv.y * (1.0 / u_size.z), 1.0));
    vec3 col = vec3(0.0);
    float dist = rayMarch(ro, rd);
    col += dist * 0.05;
    vec3 viewDir = normalize(ro - rd);

    if (dist < MAX_DIST) {
        vec3 pos = ro + dist * rd;
        vec3 normal = calcNormal(pos) * 0.95;
        float diff = dot(vec3(1.0), normal);
        vec3 light = vec3(0.0);
        light += ambientLight(vec3(1.0), 1.0);
        light += directionalLight(u_sphereCol * 0.08, 1.0, normal, vec3(1, 1, -2.0), viewDir, 0.0);
        col = u_sphereCol;
        col *= light;
    } else {
        col = u_bgColor;
    }

    gl_FragColor = vec4(col, 1.0);
}
